<template>
  <div ref="scrollTarget"></div>
  <section style="margin-top: 20px; margin-bottom: 100px;">
    <div class="container-fluid" style="max-width: 1400px;">

      <!-- En-tete -->
      <div class="pc-head" v-if="loaded">
        <div>
          <router-link to="/shipments-pending" class="pc-back">&larr; {{ t('pc_back') }}</router-link>
          <h4 class="mb-1">{{ t('pc_title') }} #{{ shipment.id }}
            <span class="badge" :class="shipment.is_pending ? 'bg-warning text-dark' : 'bg-secondary'">
              {{ shipment.is_pending ? t('pc_pending') : t('pc_not_pending') }}
            </span>
          </h4>
          <div class="text-muted small">
            {{ shipment.order_reference }} &middot; {{ t('pc_created') }} {{ shipment.created_at }}
            &middot; {{ service_point.company_name }} ({{ service_point.store_name }}, {{ service_point.user_email }})
          </div>
        </div>
        <div class="pc-money">
          <div><span class="text-muted">{{ t('pc_paid_price') }}</span> <strong>{{ shipment.sale_price }} &euro;</strong></div>
          <div><span class="text-muted">{{ t('pc_payment') }}</span> <strong>{{ paymentLabel }}</strong></div>
          <div><span class="text-muted">{{ t('pc_tracking') }}</span>
            <strong :class="shipment.tracking_number ? 'text-success' : 'text-danger'">
              {{ shipment.tracking_number || t('pc_no_tracking') }}</strong>
            <span v-if="shipment.tracking_number" class="text-muted"> ({{ shipment.transporter }})</span>
          </div>
        </div>
      </div>

      <div v-if="!loaded" class="text-center p-5">{{ t('pc_loading') }}</div>

      <div v-if="loaded && !shipment.is_pending" class="alert alert-secondary mt-3">
        {{ t('pc_not_pending_help') }}
      </div>

      <div v-if="loaded" class="row g-4 mt-1">

        <!-- ETAPE 1 : COLIS -->
        <div class="col-lg-6">
          <div class="pc-card">
            <div class="pc-card-title"><span class="pc-step">1</span> {{ t('pc_step_package') }}</div>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-bold">{{ t('package_type') }}</label>
                <select class="form-select" v-model="form.package_type" :disabled="!shipment.is_pending" @change="onPackageChange">
                  <option v-for="p in package_types" :key="p.name" :value="p.name">{{ p.name }} ({{ p.weight }} kg)</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-bold">{{ t('weight') }}</label>
                <input type="number" step="0.01" min="0.1" max="25" class="form-control" v-model="form.weight"
                  :disabled="!shipment.is_pending" @change="quote">
                <div class="form-text" v-if="parseFloat(form.weight) <= 0.5">{{ t('pc_document_only') }}</div>
                <div class="form-text text-danger" v-else>{{ t('pc_customs_warning') }}</div>
              </div>
              <div class="col-12">
                <label class="form-label fw-bold">{{ t('pc_description') }}</label>
                <input type="text" class="form-control" v-model="form.description" :disabled="!shipment.is_pending"
                  :placeholder="t('pc_description_placeholder')">
                <div class="form-text">{{ t('pc_description_help') }}</div>
              </div>
              <div class="col-md-6">
                <div class="form-check mt-2">
                  <input class="form-check-input" type="checkbox" id="pcInsurance" v-model="form.insurance_option"
                    :disabled="!shipment.is_pending" @change="quote">
                  <label class="form-check-label" for="pcInsurance">{{ t('pc_insurance') }}</label>
                </div>
                <div class="small text-muted mt-1">{{ t('pc_express') }} : {{ shipment.express ? 'Oui' : 'Non' }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-bold">{{ t('pc_price_mode') }}</label>
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="pmRecalc" value="recalculate" v-model="form.price_mode" :disabled="!shipment.is_pending">
                  <label class="form-check-label" for="pmRecalc">{{ t('pc_price_recalculate') }}</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="pmKeep" value="keep" v-model="form.price_mode" :disabled="!shipment.is_pending">
                  <label class="form-check-label" for="pmKeep">{{ t('pc_price_keep') }}</label>
                </div>
              </div>

              <div class="col-12">
                <div class="pc-quote" :class="quoteClass">
                  <template v-if="quoteResult">
                    <div><strong>{{ t('pc_new_price') }} : {{ quoteResult.new_price.toFixed(2) }} &euro;</strong>
                      &nbsp;({{ t('pc_paid_price') }} {{ quoteResult.paid_price.toFixed(2) }} &euro;)</div>
                    <div v-if="quoteResult.difference > 0">{{ t('pc_diff_more') }} : <strong>+{{ quoteResult.difference.toFixed(2) }} &euro;</strong> — {{ t('pc_diff_more_help') }}</div>
                    <div v-else-if="quoteResult.difference < 0">{{ t('pc_diff_less') }} : <strong>{{ quoteResult.difference.toFixed(2) }} &euro;</strong> — {{ t('pc_diff_less_help') }}</div>
                    <div v-else>{{ t('pc_diff_none') }}</div>
                  </template>
                  <template v-else-if="quoteError">{{ quoteError }}</template>
                  <template v-else>{{ t('pc_quote_hint') }}</template>
                </div>
              </div>

              <div class="col-12">
                <label class="form-label fw-bold">{{ t('pc_reason') }}</label>
                <input type="text" class="form-control" v-model="form.reason" :disabled="!shipment.is_pending"
                  :placeholder="t('pc_reason_placeholder')">
              </div>
              <div class="col-12 text-end">
                <button class="btn btn-pink" :disabled="!shipment.is_pending || saving" @click="saveEdit">
                  {{ t('pc_save') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ADRESSES -->
        <div class="col-lg-6">
          <div class="pc-card">
            <div class="pc-card-title">{{ t('pc_addresses') }}</div>
            <div class="row g-3">
              <div class="col-md-6" v-for="role in ['sender','recipient']" :key="role">
                <div class="fw-bold mb-2" style="color:#E5097F;">{{ role === 'sender' ? t('sender') : t('pc_recipient') }}</div>
                <input class="form-control form-control-sm mb-1" v-model="form[role].first_name" :placeholder="t('first_name')" :disabled="!shipment.is_pending">
                <input class="form-control form-control-sm mb-1" v-model="form[role].last_name" :placeholder="t('last_name')" :disabled="!shipment.is_pending">
                <input class="form-control form-control-sm mb-1" v-model="form[role].company_name" placeholder="Company" :disabled="!shipment.is_pending">
                <input class="form-control form-control-sm mb-1" v-model="form[role].email" :placeholder="t('email')" :disabled="!shipment.is_pending">
                <input class="form-control form-control-sm mb-1" v-model="form[role].phone" placeholder="Phone" :disabled="!shipment.is_pending">
                <div class="d-flex gap-1 mb-1">
                  <input class="form-control form-control-sm" style="width:30%" v-model="form[role].number" :placeholder="t('number')" :disabled="!shipment.is_pending">
                  <input class="form-control form-control-sm" v-model="form[role].street" :placeholder="t('street')" :disabled="!shipment.is_pending">
                </div>
                <div class="d-flex gap-1 mb-1">
                  <input class="form-control form-control-sm" style="width:40%" v-model="form[role].postal_code" :placeholder="t('postal_code')" :disabled="!shipment.is_pending">
                  <input class="form-control form-control-sm" v-model="form[role].city" :placeholder="t('city')" :disabled="!shipment.is_pending">
                </div>
                <input class="form-control form-control-sm mb-1" v-model="form[role].state" :placeholder="t('state')" :disabled="!shipment.is_pending">
                <input class="form-control form-control-sm mb-1" v-model="form[role].country" placeholder="Country" :disabled="!shipment.is_pending || role === 'sender'" @change="quote">
              </div>
              <div class="col-12 small text-muted">{{ t('pc_addresses_help') }}</div>
            </div>
          </div>
        </div>

        <!-- ETAPE 2 : RESERVATION -->
        <div class="col-lg-6">
          <div class="pc-card">
            <div class="pc-card-title"><span class="pc-step">2</span> {{ t('pc_step_booking') }}</div>
            <div v-if="shipment.tracking_number" class="alert alert-success py-2">
              {{ t('pc_booked_with') }} <strong>{{ shipment.transporter }}</strong> — {{ shipment.tracking_number }}
              <span v-if="shipment.shipping_date"> &middot; {{ t('shipping_date') }} {{ shipment.shipping_date }}</span>
              <span v-if="shipment.delivery_date"> &middot; {{ t('delivery_date') }} {{ shipment.delivery_date }}</span>
            </div>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-bold">{{ t('shipping_date') }}</label>
                <input type="date" class="form-control" v-model="booking.ship_date" :min="today" :disabled="!shipment.is_pending" @change="onShipDateChange">
              </div>
              <div class="col-md-6">
                <label class="form-label fw-bold">{{ t('delivery_date') }}</label>
                <div class="input-group">
                  <input type="date" class="form-control" v-model="booking.delivery_time" :disabled="!shipment.is_pending">
                  <button class="btn btn-outline-secondary" type="button" :disabled="!booking.ship_date || loadingDelivery || !shipment.is_pending" @click="getDeliveryDate">
                    {{ loadingDelivery ? '…' : t('pc_get_delivery') }}
                  </button>
                </div>
                <div class="form-text" v-if="suggestedTransporter">{{ t('pc_suggested') }} : <strong>{{ suggestedTransporter }}</strong></div>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-bold">{{ t('pc_transporter') }}</label>
                <select class="form-select" v-model="booking.transporter" :disabled="!shipment.is_pending">
                  <option v-for="tr in transporters" :key="tr" :value="tr">{{ tr }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-bold">{{ t('pc_ready_time') }}</label>
                <select class="form-select" v-model="booking.ready_time" :disabled="!shipment.is_pending">
                  <option v-for="h in readyTimes" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-bold">{{ t('pc_close_time') }}</label>
                <select class="form-select" v-model="booking.close_time" :disabled="!shipment.is_pending">
                  <option v-for="h in closeTimes" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col-12 text-end">
                <button class="btn btn-pink" :disabled="!canBook || booking_in_progress" @click="confirmBook">
                  {{ booking_in_progress ? t('pc_booking') : (shipment.tracking_number ? t('pc_rebook') : t('pc_book')) }}
                </button>
              </div>
              <div class="col-12 small text-muted">{{ t('pc_booking_help') }}</div>
            </div>
          </div>
        </div>

        <!-- ETAPE 3 : VALIDATION + DOCUMENTS + HISTORIQUE -->
        <div class="col-lg-6">
          <div class="pc-card">
            <div class="pc-card-title"><span class="pc-step">3</span> {{ t('pc_step_validate') }}</div>
            <p class="small text-muted">{{ t('pc_validate_help') }}</p>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <button class="btn btn-success" :disabled="!shipment.is_pending || !shipment.tracking_number || validating" @click="confirmValidate">
                {{ validating ? '…' : t('pc_validate') }}
              </button>
              <span v-if="!shipment.tracking_number" class="text-danger small">{{ t('pc_validate_blocked') }}</span>
            </div>
            <div class="mt-3">
              <span class="fw-bold">{{ t('pc_documents') }} :</span>
              <a v-if="documents.saleticket" href="#" @click.prevent="openDoc('saleticket', documents.saleticket_id)">{{ t('pc_sale_ticket') }}</a>
              <span v-else class="text-muted">{{ t('pc_sale_ticket') }} ({{ t('pc_missing') }})</span>
              &middot;
              <a v-if="documents.shippinglabel" href="#" @click.prevent="openDoc('label', shipment.id)">{{ t('pc_label') }}</a>
              <span v-else class="text-muted">{{ t('pc_label') }} ({{ t('pc_missing') }})</span>
              <template v-if="proforma">
                &middot;
                <a v-if="documents.proformainvoice" href="#" @click.prevent="openDoc('proforma', shipment.id)">Proforma</a>
                <span v-else class="text-muted">Proforma ({{ t('pc_missing') }})</span>
              </template>
            </div>
          </div>

          <div class="pc-card mt-3">
            <div class="pc-card-title">{{ t('pc_history') }}</div>
            <div v-if="!history.length" class="text-muted small">{{ t('pc_no_history') }}</div>
            <table v-else class="table table-sm mb-0">
              <tbody>
                <tr v-for="h in history" :key="h.id">
                  <td class="text-nowrap small">{{ h.created_at }}</td>
                  <td><span class="badge" :style="{ backgroundColor: actionColor(h.action) }">{{ actionLabel(h.action) }}</span></td>
                  <td class="small">{{ h.performed_by }}</td>
                  <td class="small">{{ h.reason }}<span v-if="h.carrier_result"> — {{ h.carrier_result }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import en from '../en.js'
import fr from '../fr.js'
import nl from '../nl.js'

const EMPTY_CLIENT = () => ({ first_name: '', last_name: '', company_name: '', email: '', phone: '', number: '', street: '', postal_code: '', state: '', city: '', country: '' })

export default {
  name: 'PendingShipmentControl',
  mixins: [en, fr, nl],
  data() {
    return {
      loaded: false,
      shipment: {},
      sender: {},
      recipient: {},
      service_point: {},
      payment: {},
      proforma: null,
      documents: {},
      package_types: [],
      transporters: ['FEDEX', 'UPS', 'CIT'],
      history: [],
      form: { package_type: '', weight: '', description: '', insurance_option: false, price_mode: 'recalculate', reason: '', sender: EMPTY_CLIENT(), recipient: EMPTY_CLIENT() },
      quoteResult: null,
      quoteError: '',
      saving: false,
      booking: { ship_date: '', delivery_time: '', transporter: '', ready_time: '09:00', close_time: '17:00' },
      suggestedTransporter: '',
      loadingDelivery: false,
      booking_in_progress: false,
      validating: false,
    }
  },
  computed: {
    lang() { return this.$store.state.lang },
    today() { return new Date().toISOString().slice(0, 10) },
    paymentLabel() {
      const m = { credit_card: this.t('pc_pay_card'), bancontact: 'Bancontact', cash: this.t('pc_pay_cash'), not_paid: this.t('pc_pay_none') }
      return m[this.payment.mode] || this.payment.mode
    },
    quoteClass() {
      if (!this.quoteResult) return ''
      if (this.quoteResult.difference > 0) return 'pc-quote-more'
      if (this.quoteResult.difference < 0) return 'pc-quote-less'
      return 'pc-quote-same'
    },
    readyTimes() {
      const out = []
      for (let h = 9; h < 17; h++) { out.push(`${String(h).padStart(2, '0')}:00`); out.push(`${String(h).padStart(2, '0')}:30`) }
      return out
    },
    closeTimes() {
      const [rh, rm] = (this.booking.ready_time || '09:00').split(':').map(Number)
      const start = rh * 60 + rm + 60
      const out = []
      for (let m = start; m <= 18 * 60; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
      return out
    },
    canBook() {
      return this.shipment.is_pending && this.booking.ship_date && this.booking.delivery_time && this.booking.transporter && this.booking.ready_time && this.booking.close_time
    },
  },
  watch: {
    'booking.ready_time'() { if (!this.closeTimes.includes(this.booking.close_time)) this.booking.close_time = this.closeTimes[this.closeTimes.length - 1] },
  },
  async mounted() {
    document.title = 'Dashboard | Pending shipment control'
    window.scrollTo(0, 0)
    await this.load()
  },
  methods: {
    t(prop) {
      const dict = this[this.lang] || this.en || {}
      return dict[prop] || (this.en && this.en[prop]) || prop
    },
    apiBase() { return axios.defaults.baseURL || '' },
    async load() {
      this.$store.commit('setIsLoading', true)
      try {
        const res = await axios.get(`/api/v1/admin/pending/${this.$route.params.shipment_id}/`)
        this.applyDetails(res.data)
        this.loaded = true
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.errorText(error), confirmButtonColor: '#ff0e0e' })
      }
      this.$store.commit('setIsLoading', false)
    },
    applyDetails(d) {
      this.shipment = d.shipment
      this.sender = d.sender
      this.recipient = d.recipient
      this.service_point = d.service_point
      this.payment = d.payment
      this.proforma = d.proforma
      this.documents = d.documents
      this.package_types = d.package_types
      this.transporters = d.transporters || this.transporters
      this.history = d.history || []
      this.form.package_type = d.shipment.package_type
      this.form.weight = d.shipment.weight
      this.form.description = d.shipment.description
      this.form.insurance_option = d.shipment.insurance_option
      this.form.sender = Object.assign(EMPTY_CLIENT(), d.sender)
      this.form.recipient = Object.assign(EMPTY_CLIENT(), d.recipient)
      if (d.shipment.shipping_date) this.booking.ship_date = d.shipment.shipping_date
      if (d.shipment.delivery_date) this.booking.delivery_time = d.shipment.delivery_date
      if (d.shipment.transporter && d.shipment.transporter !== 'EMPTY') this.booking.transporter = d.shipment.transporter
      if (d.price_now && d.price_now.new_price !== null) {
        this.quoteResult = { new_price: d.price_now.new_price, paid_price: parseFloat(d.shipment.sale_price), difference: d.price_now.difference }
      }
    },
    errorText(error) {
      if (error.response && error.response.data) return error.response.data.message || error.response.data.error || JSON.stringify(error.response.data)
      return 'Something went wrong. Please try again'
    },
    onPackageChange() {
      const p = this.package_types.find(x => x.name === this.form.package_type)
      if (p) this.form.weight = p.weight
      this.quote()
    },
    async quote() {
      this.quoteError = ''
      try {
        const res = await axios.post(`/api/v1/admin/pending/${this.shipment.id}/quote/`, {
          package_type: this.form.package_type, weight: this.form.weight,
          country: this.form.recipient.country, insurance_option: this.form.insurance_option,
        })
        this.quoteResult = res.data
      } catch (error) {
        this.quoteResult = null
        this.quoteError = this.errorText(error)
      }
    },
    async saveEdit() {
      this.saving = true
      this.$store.commit('setIsLoading', true)
      try {
        const res = await axios.post(`/api/v1/admin/pending/${this.shipment.id}/edit/`, {
          package_type: this.form.package_type, weight: this.form.weight, description: this.form.description,
          insurance_option: this.form.insurance_option, price_mode: this.form.price_mode, reason: this.form.reason,
          sender: this.form.sender, recipient: this.form.recipient,
        })
        this.applyDetails(res.data)
        const diff = res.data.price_difference
        Swal.fire({ icon: 'success', title: this.t('pc_saved'), text: diff ? `${this.t('pc_diff_recorded')} : ${diff > 0 ? '+' : ''}${diff.toFixed(2)} €` : '', confirmButtonColor: '#4BB543' })
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.errorText(error), confirmButtonColor: '#ff0e0e' })
      }
      this.saving = false
      this.$store.commit('setIsLoading', false)
    },
    onShipDateChange() { this.booking.delivery_time = ''; this.suggestedTransporter = '' },
    async getDeliveryDate() {
      this.loadingDelivery = true
      try {
        const res = await axios.post('/api/v1/get-delivery-date/', { shipment_id: this.shipment.id, ship_date: this.booking.ship_date })
        this.booking.delivery_time = res.data.delivery_time
        this.suggestedTransporter = res.data.transporter
        if (res.data.transporter && res.data.transporter !== 'EMPTY') this.booking.transporter = res.data.transporter
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.errorText(error), confirmButtonColor: '#ff0e0e' })
      }
      this.loadingDelivery = false
    },
    confirmBook() {
      Swal.fire({
        icon: 'question', title: this.t('pc_book'), text: `${this.booking.transporter} · ${this.booking.ship_date} → ${this.booking.delivery_time} · ${this.booking.ready_time}-${this.booking.close_time}`,
        showCancelButton: true, confirmButtonText: 'Yes', cancelButtonText: 'No', confirmButtonColor: '#4BB543', cancelButtonColor: '#ff0e0e',
      }).then(r => { if (r.isConfirmed) this.book() })
    },
    async book() {
      this.booking_in_progress = true
      this.$store.commit('setIsLoading', true)
      try {
        const res = await axios.post(`/api/v1/admin/pending/${this.shipment.id}/book/`, { ...this.booking, reason: this.form.reason })
        await this.load()
        Swal.fire({ icon: 'success', title: this.t('pc_booked'), text: res.data.tracking_number || '', confirmButtonColor: '#4BB543' })
      } catch (error) {
        await this.load()
        Swal.fire({ icon: 'error', title: this.t('pc_booking_failed'), text: this.errorText(error), confirmButtonColor: '#ff0e0e' })
      }
      this.booking_in_progress = false
      this.$store.commit('setIsLoading', false)
    },
    confirmValidate() {
      Swal.fire({
        icon: 'question', title: this.t('pc_validate'), text: this.t('pc_validate_confirm'),
        showCancelButton: true, confirmButtonText: 'Yes', cancelButtonText: 'No', confirmButtonColor: '#4BB543', cancelButtonColor: '#ff0e0e',
      }).then(r => { if (r.isConfirmed) this.validate() })
    },
    async validate() {
      this.validating = true
      this.$store.commit('setIsLoading', true)
      try {
        await axios.post(`/api/v1/admin/pending/${this.shipment.id}/validate/`, { reason: this.form.reason })
        await Swal.fire({ icon: 'success', title: this.t('pc_validated'), confirmButtonColor: '#4BB543' })
        this.$router.push('/shipments-pending')
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.errorText(error), confirmButtonColor: '#ff0e0e' })
      }
      this.validating = false
      this.$store.commit('setIsLoading', false)
    },
    openDoc(kind, id) {
      const base = this.apiBase()
      const map = { saleticket: `/api/v1/saleticket/print/${id}/`, label: `/api/v1/shhippinglabel/print/${id}/`, proforma: `/api/v1/proformainvoice/print/${id}/` }
      window.open(base + map[kind], '_blank')
    },
    actionLabel(a) {
      const m = { CANCEL: 'Annulation', DELETE: 'Suppression', REACTIVATE: 'Réactivation', ADMIN_EDIT: this.t('pc_act_edit'), ADMIN_BOOK: this.t('pc_act_book'), ADMIN_VALIDATE: this.t('pc_act_validate') }
      return m[a] || a
    },
    actionColor(a) {
      const m = { CANCEL: '#fd7e14', DELETE: '#dc3545', REACTIVATE: '#28a745', ADMIN_EDIT: '#0d6efd', ADMIN_BOOK: '#6f42c1', ADMIN_VALIDATE: '#198754' }
      return m[a] || '#6c757d'
    },
  }
}
</script>

<style scoped>
.pc-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; padding: 16px 20px; background: #FEEEF6; border-radius: 12px; }
.pc-back { color: #E5097F; font-weight: 600; text-decoration: none; font-size: 14px; }
.pc-money { text-align: right; font-size: 15px; line-height: 1.7; }
.pc-card { background: #fff; border: 1px solid #e6e6e6; border-radius: 12px; padding: 18px 20px; height: 100%; box-shadow: 1px 1px 3px #eee; }
.pc-card-title { font-weight: 700; font-size: 17px; margin-bottom: 14px; color: #222; display: flex; align-items: center; gap: 8px; }
.pc-step { display: inline-flex; width: 26px; height: 26px; border-radius: 50%; background: #E5097F; color: #fff; align-items: center; justify-content: center; font-size: 14px; }
.pc-quote { border-radius: 8px; padding: 10px 14px; background: #f5f5f5; font-size: 14px; }
.pc-quote-more { background: #fff3cd; }
.pc-quote-less { background: #d1e7dd; }
.pc-quote-same { background: #e9ecef; }
.btn-pink { background: #E5097F; color: #fff; font-weight: 600; border: none; border-radius: 20px; padding: 8px 22px; }
.btn-pink:hover { background: #9b0656; color: #fff; }
.btn-pink:disabled { background: #f1a8d0; }
.form-label { font-size: 14px; }
</style>
